import * as React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Theme, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useEffect, useContext } from 'react';
import ImageCarousel from '../../../utils/components/ImageCarousel';
import { ReportViewContext } from '../../../store/reportViewContext';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%',
    padding: theme.spacing(5),
  },
  formItem: {
    minHeight: 120,
    whiteSpace: 'pre-line',
    [theme.breakpoints.down('lg')]: {
      minHeight: 'unset',
    },
  },
  button: {
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  chip: {
    margin: theme.spacing(0.5, 0.5, 0.5, 0),
  },
}));

type Props = {
  report: VptReport;
};

const Body: React.FC<Props> = ({ report }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getReportDetails, getReportImageModels } = useContext(
    ReportViewContext
  );

  const [reportDetails, setReportDetails] = React.useState<ReportDetail | null>(
    null
  );
  const [reportImageModels, setReportImageModels] = React.useState<
    ReportImageModel[] | null
  >(null);

  useEffect(() => {
    if (report.id >= 0) {
      getReportDetails(report.id, false, setReportDetails);
      getReportImageModels(report.id, false, setReportImageModels);
    }
  }, [report.id, getReportDetails, getReportImageModels]);

  const reportLogs = report.inventory ? report.inventoryLogs : report.stockingLogs;
  const logs = Array.isArray(reportLogs) ? reportLogs : [];
  const missingProducts = logs.filter((log) => (log.amountMissing ?? 0) > 0);
  const stockedProducts = logs.filter((log) => (log.amountAdded ?? 0) > 0);

  return (
    <Grid container spacing={2} >
      <Grid size={{ lg: 6, xs: 12 }} className={classes.formItem}>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            {t('products:t.missingProducts')}
          </Typography>
          {missingProducts.length > 0 ? (
            missingProducts.map((log, index) => (
              <Typography key={`${log.productCode}-missing-${index}`} variant="body2" sx={{ mt: 1 }}>
                {log.productCode} ({log.amountMissing})
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              -
            </Typography>
          )}
          <Typography variant="subtitle1">{t('products:t.stockedProducts')}</Typography>
          {stockedProducts.length > 0 ? (
              stockedProducts.map((log, index) => (
                  <Typography key={`${log.productCode}-added-${index}`} variant="body2" sx={{ mt: 1 }}>
                    {log.productCode} (+{log.amountAdded})
                  </Typography>
              ))
          ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                -
              </Typography>
          )}
      </Grid>
      <Grid size={{ lg: 6, xs: 12 }} className={classes.formItem}>
          <Typography variant="subtitle1">Notes</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
            {reportDetails?.body || '-'}
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Attached images
          </Typography>
          {reportImageModels ? (
              <ImageCarousel imageModels={reportImageModels} />
          ) : (
              <Typography variant="body2" color="text.secondary" >
                {t('reports:t.noImages')}
              </Typography>
          )}
      </Grid>
    </Grid>
  );
};

export default Body;
